import { UserPlusIcon, XMarkIcon, CheckCircleIcon, ExclamationCircleIcon, ShieldCheckIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import SecurityAnalystForm from "./SecurityAnalystForm";
import AuditorForm from "./AuditorForm";
import AdminForm from "./AdminForm";
import CustomerAdminForm from "./CustomerAdminForm";
import EndUserForm from "./EndUserForm";
import { toast } from "../../utils/notify";
import { saveUserApi } from "../../services/axios/AuthApis/AdministrationApis";
import { useAuth } from "../../context/AuthContext";
import { useEffect, useState } from "react";

const APP_PRIMARY = "var(--app-primary-color, #26256b)";

const formatRole = (role) =>
  role ? role.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "User";

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

// ── Progress overlay ────────────────────────────────────────────────────────
const ProgressOverlay = ({ steps, current, status }) => (
  <div className="flex flex-col items-center justify-center px-8 py-10 w-full">
    <div className="w-full max-w-sm space-y-3">
      {steps.map((label, i) => {
        const isDone = current > i || status === 'success';
        const isActive = current === i && status === 'running';
        const isError = status === 'error' && current === i;

        return (
          <motion.div
            key={label}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.08 }}
            className="flex items-center gap-3"
          >
            <div className="shrink-0 w-7 h-7 flex items-center justify-center">
              {isError ? (
                <ExclamationCircleIcon className="w-6 h-6 text-red-500" />
              ) : isDone ? (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300 }}>
                  <CheckCircleIcon className="w-6 h-6 text-green-500" />
                </motion.div>
              ) : isActive ? (
                <svg className="animate-spin w-5 h-5" style={{ color: APP_PRIMARY }} fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-gray-200" />
              )}
            </div>
            <span className={`text-sm font-medium transition-colors ${
              isError ? 'text-red-500' : isDone ? 'text-green-600' : isActive ? 'text-gray-900' : 'text-gray-400'
            }`}>
              {label}
            </span>
          </motion.div>
        );
      })}

      {/* Overall status message */}
      <AnimatePresence>
        {status === 'success' && (
          <motion.p
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-sm font-semibold text-green-600 pt-3"
          >
            Done!
          </motion.p>
        )}
        {status === 'error' && (
          <motion.p
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-sm font-semibold text-red-500 pt-3"
          >
            Something went wrong. Please try again.
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  </div>
);

// ── Main component ──────────────────────────────────────────────────────────
const CreateUserModal = ({
  setShowCreateUserModal,
  newUser,
  setNewUser,
  errors,
  setErrors,
  isViewMode,
  editingUserId,
  employeeConfig,
  showDropdown,
  setShowDropdown,
  setIsViewMode,
  setEditingUserId,
  blankUser,
  setShowRoleSelectionModal,
  getRoleColor,
  userPagination,
  setUserPagination,
  refreshUsers,
}) => {
  const { user } = useAuth();
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState("");
  const [progress, setProgress] = useState({ active: false, steps: [], current: -1, status: 'idle' });

  const CREATE_STEPS = ["Validating form data", "Creating user account", "Setting up permissions", "Sending welcome email"];
  const UPDATE_STEPS = ["Validating changes", "Updating user profile", "Syncing permissions"];

  useEffect(() => {
    if (!newUser?.profile_photo) { setProfilePreviewUrl(""); return; }
    if (typeof newUser.profile_photo === "string") { setProfilePreviewUrl(newUser.profile_photo); return; }
    if (newUser.profile_photo instanceof File) {
      const url = URL.createObjectURL(newUser.profile_photo);
      setProfilePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [newUser?.profile_photo]);

  const handleClose = () => {
    setShowCreateUserModal(false);
    setIsViewMode(false);
    setEditingUserId(null);
    setNewUser(blankUser);
    setProgress({ active: false, steps: [], current: -1, status: 'idle' });
  };

  const handleValidateAndSaveUser = (e) => {
    e.preventDefault();
    const newErrors = {};
    const nameRegex = /^[A-Za-z .']{1,250}$/;
    const emailRegex = /^[A-Za-z0-9._+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    const companyRegex = /^[A-Za-z0-9 ._&,-]{1,250}$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!newUser.firstname?.trim()) newErrors.firstname = "First name is required";
    else if (!nameRegex.test(newUser.firstname)) newErrors.firstname = "Only alphabets, space, dot, apostrophe allowed. Max 250 chars.";
    if (newUser.lastname && !nameRegex.test(newUser.lastname)) newErrors.lastname = "Only alphabets, space, dot, apostrophe allowed. Max 250 chars.";
    if (!newUser.email?.trim()) newErrors.email = "Email is required";
    else if (newUser.email.length > 250 || !emailRegex.test(newUser.email)) newErrors.email = "Invalid email format.";
    if (newUser.customername && !nameRegex.test(newUser.customername)) newErrors.customername = "Only alphabets, space, dot, apostrophe allowed.";
    if (newUser.company && !companyRegex.test(newUser.company)) newErrors.company = "Only alphabets, numbers, spaces, ., _, -, &, , allowed.";
    if (newUser.phone && !phoneRegex.test(newUser.phone)) newErrors.phone = "Phone must be exactly 10 digits";
    if (newUser.location && newUser.location.length > 250) newErrors.location = "Location cannot exceed 250 characters";
    if (newUser.role === "customer_admin") {
      if (!newUser.company?.trim()) newErrors.company = "Company name is required";
      if (!newUser.employeecount) newErrors.employeecount = "Employee count required";
      if (!newUser.price || parseFloat(newUser.price) < 0) newErrors.price = "Valid price required";
      if (!newUser.logoImg) newErrors.logoImg = "Logo image required";
    }
    if (newUser.role === "auditor" || newUser.role === "security_analyst") {
      if (selectedCompanies.length === 0) newErrors.company = "Company name is required";
    }
    if (newUser.role === "end_user") {
      if (!newUser.company?.trim()) newErrors.company = "Company is required";
      if (!newUser.date_of_joining) newErrors.date_of_joining = "Date of Joining is required";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) handleSaveUser(e);
    else toast.error("Please fix the highlighted errors.");
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();

    const isUpdate = !!editingUserId;
    const steps = isUpdate ? UPDATE_STEPS : CREATE_STEPS;
    setSaving(true);
    setProgress({ active: true, steps, current: 0, status: 'running' });

    await sleep(400); // step 0 — validating

    const payload = {
      firstname: newUser.firstname || "",
      lastname: newUser.lastname || "",
      email: newUser.email || "",
      role: newUser.role || "end_user",
      department: newUser.department || "",
      phone: newUser.phone || "",
      location: newUser.location || "",
      status: newUser.status || "active",
      customername: newUser.customername || "",
      reporting_to: newUser.reporting_to || "",
      uid: newUser.uid || "",
      created_by: newUser.created_by || user?.email || "system",
      updated_by: user?.email || "system",
      created_at: newUser.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_login: isUpdate ? newUser.last_login : null,
    };
    if (newUser.role === "security_analyst" || newUser.role === "auditor") {
      payload.company_ids = selectedCompanies.map((c) => c.id);
    }
    if (newUser.role === "customer_admin") {
      payload.company = newUser.company || "";
      payload.employeecount = newUser.employeecount || "";
      payload.price = newUser.price || "";
      payload.logoImg = newUser.logoImg || "";
      if (newUser.renewal_date) {
        payload.renewal_date = newUser.renewal_date;
      } else {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 1);
        payload.renewal_date = d.toISOString().split("T")[0];
      }
    } else if (newUser.role === "end_user") {
      payload.company = newUser.company || "";
      payload.date_of_joining = newUser.date_of_joining || "";
    }
    if (newUser.role !== "customer_admin" && newUser.profile_photo instanceof File) {
      payload.profile_photo = newUser.profile_photo;
    }

    // step 1 — API call
    setProgress((p) => ({ ...p, current: 1 }));
    try {
      const response = await saveUserApi(payload, editingUserId);

      if (response.status === 200 || response.status === 201) {
        // step 2 — permissions / finalizing
        setProgress((p) => ({ ...p, current: 2 }));
        await sleep(350);

        // step 3 — welcome email (create only)
        if (!isUpdate) {
          setProgress((p) => ({ ...p, current: 3 }));
          await sleep(350);
        }

        // The create/update endpoint's response shape differs by role
        // (security_analyst/auditor return no user payload at all; other
        // roles nest it under "data") and never matches the flat shape the
        // users-list GET endpoint returns, so refresh from that canonical
        // API instead of inserting the raw response into state.
        if (refreshUsers) {
          await refreshUsers();
        }

        setProgress((p) => ({ ...p, current: steps.length, status: 'success' }));
        await sleep(900);
        toast.success(isUpdate ? "User updated successfully!" : "User created successfully!");
        handleClose();
      }
    } catch (err) {
      console.error(err.response?.data || err.message);
      setProgress((p) => ({ ...p, status: 'error' }));
      await sleep(1200);
      setProgress({ active: false, steps: [], current: -1, status: 'idle' });
      toast.error(err.response?.data?.error || err.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const title = isViewMode ? "View User" : editingUserId ? "Update User" : `Create ${formatRole(newUser.role)}`;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden relative"
      >
        {/* Header */}
        <div
          className="px-6 py-5 flex items-center justify-between shrink-0"
          style={{ background: `linear-gradient(135deg, color-mix(in srgb, ${APP_PRIMARY} 90%, black 10%), ${APP_PRIMARY})` }}
        >
          <div className="flex items-center gap-3">
            <div className="bg-white/15 rounded-xl p-2">
              <UserPlusIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">{title}</h3>
              {newUser.role && (
                <p className="text-xs text-white/70 mt-0.5">{formatRole(newUser.role)}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={saving}
            className="text-white/70 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body wrapper — overlay is sibling to scroll container so it covers the full area */}
        <div className="relative">
          <AnimatePresence>
            {progress.active && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 bg-white/95 backdrop-blur-sm z-10 flex items-center justify-center rounded-b-2xl"
                style={{ minHeight: '280px' }}
              >
                <ProgressOverlay
                  steps={progress.steps}
                  current={progress.current}
                  status={progress.status}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Scrollable form */}
          <div className="p-6 max-h-[75vh] overflow-y-auto">
          <form className="space-y-4" onSubmit={handleValidateAndSaveUser}>
            {/* Role badge */}
            {newUser.role && !editingUserId && (
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-xl mb-4"
                style={{ background: `linear-gradient(135deg, color-mix(in srgb, ${APP_PRIMARY} 8%, white), color-mix(in srgb, ${APP_PRIMARY} 4%, white))`, border: `1px solid color-mix(in srgb, ${APP_PRIMARY} 20%, transparent)` }}
              >
                <div
                  className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `color-mix(in srgb, ${APP_PRIMARY} 15%, white)` }}
                >
                  <ShieldCheckIcon className="w-4 h-4" style={{ color: APP_PRIMARY }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide leading-none mb-1">Selected Role</p>
                  <p className="text-sm font-semibold truncate" style={{ color: APP_PRIMARY }}>{formatRole(newUser.role)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateUserModal(false);
                    setShowRoleSelectionModal(true);
                    setNewUser(blankUser);
                  }}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{ color: APP_PRIMARY, backgroundColor: `color-mix(in srgb, ${APP_PRIMARY} 10%, white)` }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `color-mix(in srgb, ${APP_PRIMARY} 20%, white)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = `color-mix(in srgb, ${APP_PRIMARY} 10%, white)`; }}
                >
                  <ArrowPathIcon className="w-3 h-3" />
                  Change
                </button>
              </div>
            )}

            {!editingUserId && !isViewMode && !newUser.role && (
              <p className="text-sm text-gray-400 text-center py-4">Select a role to continue</p>
            )}

            <fieldset
              disabled={isViewMode}
              className={`contents ${isViewMode ? "[&_input]:cursor-not-allowed [&_select]:cursor-not-allowed [&_textarea]:cursor-not-allowed [&_input:disabled]:bg-gray-50 [&_select:disabled]:bg-gray-50 [&_textarea:disabled]:bg-gray-50 [&_input:disabled]:text-gray-700 [&_select:disabled]:text-gray-700 [&_textarea:disabled]:text-gray-700" : ""}`}
            >
              {newUser.role === "admin" && <AdminForm newUser={newUser} setNewUser={setNewUser} errors={errors} setErrors={setErrors} isEdit={!!editingUserId} />}
              {newUser.role === "customer_admin" && <CustomerAdminForm newUser={newUser} setNewUser={setNewUser} errors={errors} setErrors={setErrors} employeeConfig={employeeConfig} isViewMode={isViewMode} isEdit={!!editingUserId} />}
              {newUser.role === "auditor" && <AuditorForm newUser={newUser} setNewUser={setNewUser} errors={errors} setErrors={setErrors} selectedCompanies={selectedCompanies} setSelectedCompanies={setSelectedCompanies} isEdit={!!editingUserId} />}
              {newUser.role === "security_analyst" && <SecurityAnalystForm newUser={newUser} setNewUser={setNewUser} errors={errors} setErrors={setErrors} selectedCompanies={selectedCompanies} setSelectedCompanies={setSelectedCompanies} isEdit={!!editingUserId} />}
              {newUser.role === "end_user" && <EndUserForm newUser={newUser} setNewUser={setNewUser} errors={errors} setErrors={setErrors} isEdit={!!editingUserId} />}
            </fieldset>

            {/* Profile photo */}
            {newUser.role && newUser.role !== "customer_admin" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Profile Photo</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={isViewMode}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setNewUser({ ...newUser, profile_photo: file });
                  }}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
                <p className="text-xs text-gray-400 mt-1">Accepted: JPG, PNG, WEBP</p>
                {profilePreviewUrl && (
                  <img src={profilePreviewUrl} alt="Profile" className="mt-3 h-16 w-16 rounded-full object-cover border-2 border-gray-100 shadow-sm" />
                )}
                {newUser.profile_photo instanceof File && (
                  <p className="text-xs text-gray-500 mt-1">Selected: {newUser.profile_photo.name}</p>
                )}
              </div>
            )}

            {/* Footer buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              {!isViewMode && (
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ backgroundColor: APP_PRIMARY }}
                >
                  {saving && (
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  )}
                  {saving ? "Processing…" : editingUserId ? "Update User" : "Create User"}
                </button>
              )}
            </div>
          </form>
          </div>{/* end scrollable div */}
        </div>{/* end body wrapper */}
      </motion.div>
    </div>
  );
};

export default CreateUserModal;
